import { useCallback } from 'react';
import type React from 'react';

import type Cropper from 'cropperjs';

import { Translate, useI18n } from '@change-corgi/core-react/i18n';
import { Button } from '@change-corgi/design-system-legacy/components/actions';
import { iconClose } from '@change-corgi/design-system-legacy/icons';
import { Box } from '@change-corgi/design-system-legacy/layout';
import { Text } from '@change-corgi/design-system-legacy/typography';

import type { MediaCropArea } from '#src/app/shared/hooks/imageUpload';
import type { UploaderControls } from '#src/app/shared/hooks/uppyUploader';

import { CropperElement } from './CropperElement';

type ImageCropperProps = {
	crop?: MediaCropArea;
	disabled?: boolean;
	image?: string;
	onCancelClick?: () => void;
	onImageChange: (image?: string) => void;
	onImageCrop: (imageCrop?: MediaCropArea) => void;
	uploader: UploaderControls;
	onInitialized?: (instance: Cropper) => void;
};

export const ImageCropper: React.FC<ImageCropperProps> = ({
	crop,
	disabled = false,
	image,
	onImageChange,
	onCancelClick,
	onImageCrop,
	uploader,
	onInitialized,
}) => {
	const { translate } = useI18n();
	const onCancelReposition = useCallback(() => {
		const file = uploader.getFile();
		if (file) {
			uploader.removeFile(file.id);
		}
		onImageChange(undefined);
		onImageCrop(undefined);
		if (onCancelClick) onCancelClick();
	}, [onCancelClick, onImageChange, onImageCrop, uploader]);

	return (
		<>
			<Box sx={{ aspectRatio: '16/9', overflow: 'inherit', position: 'relative' }}>
				<CropperElement
					crop={crop}
					data-testid="cropper-element"
					disabled={disabled}
					image={image}
					updateCrop={onImageCrop}
					updateImage={uploader.updateImage}
					onInitialized={onInitialized}
				/>
				{!disabled && (
					<Button
						variant="secondaryEmphasis"
						size="small"
						data-testid="remove-img-btn"
						mr={16}
						mt={16}
						onClick={onCancelReposition}
						sx={{
							position: 'absolute',
							right: '0',
							top: '0',
							zIndex: '1',
						}}
						icon={iconClose}
						mode="icon"
					>
						{translate('corgi.sap.media.a11y.cropper.button_aria_label')}
					</Button>
				)}
			</Box>
			{!disabled && (
				<Box
					variant="bordered"
					p={8}
					backgroundColor="primary-greyBackground"
					mt={0}
					data-testid="cropper-instructions"
					sx={{ borderRadius: 0 }}
				>
					<Text size="small">
						<Translate value="corgi.sap.media.cropper.reposition" />
					</Text>
				</Box>
			)}
		</>
	);
};
